import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Save, FileDown, Info, X as XIcon, Plus, MessageSquare, Trash2 } from "lucide-react";
import {
  getClient,
  updateClient,
  getReport,
  listObservations,
  createObservation,
  deleteObservation,
  COMMON_TAGS,
} from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import ClientFiles from "../components/ClientFiles";
import { toast } from "sonner";
import { exportClientPDF } from "../lib/pdf";
import ReportDetail from "./ReportDetail";

const emptyClient = {
  name: "",
  document: "",
  email: "",
  phone: "",
  nome_fantasia: "",
  cnae: "",
  data_constituicao: "",
  inscricao_estadual: "",
  inscricao_municipal: "",
  endereco_cep: "",
  endereco_rua: "",
  endereco_numero: "",
  endereco_complemento: "",
  endereco_bairro: "",
  endereco_cidade: "",
  endereco_uf: "",
  contato_socio_nome: "",
  contato_socio_email: "",
  contato_socio_whatsapp: "",
  contato_financeiro_nome: "",
  contato_financeiro_email: "",
  contato_financeiro_whatsapp: "",
  regime_tributario: "",
  tipo_certificado: "",
  validade_certificado: "",
  banco: "",
  agencia: "",
  conta: "",
  tipo_conta: "",
  observacoes: "",
  tags: [],
  data_aniversario: "",
};

const formatDateTime = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const Section = ({ title, children }) => (
  <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 sm:p-6">
    <h2 className="font-display text-lg font-semibold text-[#0F2942] dark:text-white mb-4">
      {title}
    </h2>
    {children}
  </section>
);

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <Label>{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

export default function ClientDetail() {
  const { clientId } = useParams();
  const { canEdit, userName, isAdmin } = useAuth();
  const [form, setForm] = useState(emptyClient);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [audit, setAudit] = useState({ by: null, at: null });
  const [observations, setObservations] = useState([]);
  const [newObs, setNewObs] = useState("");
  const [obsSubmitting, setObsSubmitting] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const disabled = !canEdit;

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const load = useCallback(async () => {
    try {
      const [c, obs] = await Promise.all([
        getClient(clientId),
        listObservations(clientId).catch(() => []),
      ]);
      setForm({ ...emptyClient, ...c, tags: c.tags || [] });
      setAudit({ by: c.last_updated_by, at: c.last_updated_at });
      setObservations(obs);
    } catch {
      toast.error("Erro ao carregar cliente");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const addTag = (t) => {
    const tag = (t || tagInput).trim();
    if (!tag) return;
    if ((form.tags || []).includes(tag)) return;
    set("tags", [...(form.tags || []), tag]);
    setTagInput("");
  };
  const removeTag = (t) => set("tags", (form.tags || []).filter((x) => x !== t));

  const submitObs = async () => {
    const text = newObs.trim();
    if (!text) return;
    setObsSubmitting(true);
    try {
      const created = await createObservation(clientId, text);
      setObservations((s) => [created, ...s]);
      setNewObs("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao adicionar observação");
    } finally {
      setObsSubmitting(false);
    }
  };

  const removeObs = async (id) => {
    try {
      await deleteObservation(clientId, id);
      setObservations((s) => s.filter((o) => o.id !== id));
      toast.success("Observação excluída");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir");
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    if (!canEdit) return;
    if (!form.name.trim()) {
      toast.error("Razão Social é obrigatória");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, updated_by: userName };
      const saved = await updateClient(clientId, payload);
      setAudit({ by: saved.last_updated_by, at: saved.last_updated_at });
      toast.success("Ficha salva com sucesso");
    } catch {
      toast.error("Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const [client, report] = await Promise.all([
        getClient(clientId),
        getReport(clientId).catch(() => null),
      ]);
      await exportClientPDF(client, report);
      toast.success("PDF gerado");
    } catch (e) {
      toast.error("Erro ao gerar PDF");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center text-slate-400 text-sm">
        Carregando cliente...
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-detail-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <Link
            to="/clientes"
            data-testid="back-to-clients"
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0F2942] mb-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar para Clientes
          </Link>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#0F172A]">
            {form.name || "Cliente"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {form.document || "CNPJ não informado"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={handleExportPDF}
            disabled={exporting}
            data-testid="export-pdf-button"
          >
            <FileDown className="h-4 w-4 mr-2" />
            {exporting ? "Gerando..." : "Exportar PDF"}
          </Button>
          {canEdit && (
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-client-button"
              className="bg-[#0F2942] hover:bg-[#173E63] text-white"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          )}
        </div>
      </div>

      {!canEdit && (
        <div
          className="flex items-center gap-2 text-xs text-slate-500 bg-slate-100 px-3 py-2 rounded-lg"
          data-testid="viewer-mode-banner-client"
        >
          <Info className="h-4 w-4" />
          Modo Visualizador — campos bloqueados
        </div>
      )}

      <Tabs defaultValue="dados" className="w-full">
        <TabsList className="w-full sm:w-auto" data-testid="client-tabs">
          <TabsTrigger value="dados" data-testid="tab-dados">
            Dados do Cliente
          </TabsTrigger>
          <TabsTrigger value="ficha" data-testid="tab-ficha">
            Ficha de Processo
          </TabsTrigger>
          <TabsTrigger value="contrato" data-testid="tab-contrato">
            Contrato Social
          </TabsTrigger>
          <TabsTrigger value="documentos" data-testid="tab-documentos">
            Documentos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dados" className="space-y-6 mt-4">
          <Section title="Identificação">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Razão Social *">
                <Input
                  value={form.name}
                  disabled={disabled}
                  onChange={(e) => set("name", e.target.value)}
                  data-testid="cf-razao-social"
                />
              </Field>
              <Field label="Nome Fantasia">
                <Input
                  value={form.nome_fantasia}
                  disabled={disabled}
                  onChange={(e) => set("nome_fantasia", e.target.value)}
                  data-testid="cf-nome-fantasia"
                />
              </Field>
              <Field label="CNPJ">
                <Input
                  value={form.document}
                  disabled={disabled}
                  onChange={(e) => set("document", e.target.value)}
                  placeholder="00.000.000/0000-00"
                  data-testid="cf-cnpj"
                />
              </Field>
              <Field label="CNAE">
                <Input
                  value={form.cnae}
                  disabled={disabled}
                  onChange={(e) => set("cnae", e.target.value)}
                  data-testid="cf-cnae"
                />
              </Field>
              <Field label="Data de Constituição">
                <Input
                  type="date"
                  value={form.data_constituicao}
                  disabled={disabled}
                  onChange={(e) => set("data_constituicao", e.target.value)}
                  data-testid="cf-data-constituicao"
                />
              </Field>
              <div />
              <Field label="Inscrição Estadual">
                <Input
                  value={form.inscricao_estadual}
                  disabled={disabled}
                  onChange={(e) => set("inscricao_estadual", e.target.value)}
                  data-testid="cf-insc-estadual"
                />
              </Field>
              <Field label="Inscrição Municipal">
                <Input
                  value={form.inscricao_municipal}
                  disabled={disabled}
                  onChange={(e) => set("inscricao_municipal", e.target.value)}
                  data-testid="cf-insc-municipal"
                />
              </Field>
            </div>
          </Section>

          <Section title="Endereço">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Field label="CEP">
                <Input
                  value={form.endereco_cep}
                  disabled={disabled}
                  onChange={(e) => set("endereco_cep", e.target.value)}
                  data-testid="cf-cep"
                />
              </Field>
              <Field label="Rua" className="md:col-span-2">
                <Input
                  value={form.endereco_rua}
                  disabled={disabled}
                  onChange={(e) => set("endereco_rua", e.target.value)}
                  data-testid="cf-rua"
                />
              </Field>
              <Field label="Número">
                <Input
                  value={form.endereco_numero}
                  disabled={disabled}
                  onChange={(e) => set("endereco_numero", e.target.value)}
                  data-testid="cf-numero"
                />
              </Field>
              <Field label="Complemento" className="md:col-span-2">
                <Input
                  value={form.endereco_complemento}
                  disabled={disabled}
                  onChange={(e) => set("endereco_complemento", e.target.value)}
                  data-testid="cf-complemento"
                />
              </Field>
              <Field label="Bairro">
                <Input
                  value={form.endereco_bairro}
                  disabled={disabled}
                  onChange={(e) => set("endereco_bairro", e.target.value)}
                  data-testid="cf-bairro"
                />
              </Field>
              <Field label="Cidade">
                <Input
                  value={form.endereco_cidade}
                  disabled={disabled}
                  onChange={(e) => set("endereco_cidade", e.target.value)}
                  data-testid="cf-cidade"
                />
              </Field>
              <Field label="UF">
                <Input
                  value={form.endereco_uf}
                  disabled={disabled}
                  maxLength={2}
                  onChange={(e) => set("endereco_uf", e.target.value.toUpperCase())}
                  data-testid="cf-uf"
                />
              </Field>
            </div>
          </Section>

          <Section title="Contatos">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Sócio — Nome">
                <Input
                  value={form.contato_socio_nome}
                  disabled={disabled}
                  onChange={(e) => set("contato_socio_nome", e.target.value)}
                  data-testid="cf-socio-nome"
                />
              </Field>
              <Field label="Sócio — E-mail">
                <Input
                  type="email"
                  value={form.contato_socio_email}
                  disabled={disabled}
                  onChange={(e) => set("contato_socio_email", e.target.value)}
                  data-testid="cf-socio-email"
                />
              </Field>
              <Field label="Sócio — WhatsApp">
                <Input
                  value={form.contato_socio_whatsapp}
                  disabled={disabled}
                  onChange={(e) => set("contato_socio_whatsapp", e.target.value)}
                  placeholder="(11) 99999-9999"
                  data-testid="cf-socio-whatsapp"
                />
              </Field>
              <Field label="Financeiro — Nome">
                <Input
                  value={form.contato_financeiro_nome}
                  disabled={disabled}
                  onChange={(e) => set("contato_financeiro_nome", e.target.value)}
                  data-testid="cf-fin-nome"
                />
              </Field>
              <Field label="Financeiro — E-mail">
                <Input
                  type="email"
                  value={form.contato_financeiro_email}
                  disabled={disabled}
                  onChange={(e) => set("contato_financeiro_email", e.target.value)}
                  data-testid="cf-fin-email"
                />
              </Field>
              <Field label="Financeiro — WhatsApp">
                <Input
                  value={form.contato_financeiro_whatsapp}
                  disabled={disabled}
                  onChange={(e) => set("contato_financeiro_whatsapp", e.target.value)}
                  data-testid="cf-fin-whatsapp"
                />
              </Field>
            </div>
          </Section>

          <Section title="Regime Tributário & Certificado Digital">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Regime Tributário">
                <Select
                  value={form.regime_tributario}
                  disabled={disabled}
                  onValueChange={(v) => set("regime_tributario", v)}
                >
                  <SelectTrigger data-testid="cf-regime">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Simples Nacional">Simples Nacional</SelectItem>
                    <SelectItem value="Lucro Presumido">Lucro Presumido</SelectItem>
                    <SelectItem value="Lucro Real">Lucro Real</SelectItem>
                    <SelectItem value="MEI">MEI</SelectItem>
                    <SelectItem value="Outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Tipo de Certificado Digital">
                <Select
                  value={form.tipo_certificado}
                  disabled={disabled}
                  onValueChange={(v) => set("tipo_certificado", v)}
                >
                  <SelectTrigger data-testid="cf-tipo-certificado">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A1">A1</SelectItem>
                    <SelectItem value="A3">A3</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Validade do Certificado">
                <Input
                  type="date"
                  value={form.validade_certificado}
                  disabled={disabled}
                  onChange={(e) => set("validade_certificado", e.target.value)}
                  data-testid="cf-validade-certificado"
                />
              </Field>
            </div>
          </Section>

          <Section title="Dados Bancários">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Field label="Banco" className="md:col-span-2">
                <Input
                  value={form.banco}
                  disabled={disabled}
                  onChange={(e) => set("banco", e.target.value)}
                  data-testid="cf-banco"
                />
              </Field>
              <Field label="Agência">
                <Input
                  value={form.agencia}
                  disabled={disabled}
                  onChange={(e) => set("agencia", e.target.value)}
                  data-testid="cf-agencia"
                />
              </Field>
              <Field label="Conta">
                <Input
                  value={form.conta}
                  disabled={disabled}
                  onChange={(e) => set("conta", e.target.value)}
                  data-testid="cf-conta"
                />
              </Field>
              <Field label="Tipo de Conta" className="md:col-span-2">
                <Select
                  value={form.tipo_conta}
                  disabled={disabled}
                  onValueChange={(v) => set("tipo_conta", v)}
                >
                  <SelectTrigger data-testid="cf-tipo-conta">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Corrente">Corrente</SelectItem>
                    <SelectItem value="Poupança">Poupança</SelectItem>
                    <SelectItem value="Pagamento">Pagamento</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </Section>

          <Section title="Etiquetas e Aniversário">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Aniversário do cliente/representante</Label>
                <Input
                  type="date"
                  value={form.data_aniversario || ""}
                  disabled={disabled}
                  onChange={(e) => set("data_aniversario", e.target.value)}
                  data-testid="cf-data-aniversario"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Etiquetas</Label>
                <div className="mt-1 flex flex-wrap gap-2 min-h-[40px] p-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900">
                  {(form.tags || []).map((t) => (
                    <Badge key={t} data-testid={`tag-${t}`} className="bg-[#0F2942] text-white hover:bg-[#0F2942] border-0 flex items-center gap-1 pl-2 pr-1">
                      {t}
                      {!disabled && (
                        <button type="button" onClick={() => removeTag(t)} className="hover:bg-white/20 rounded p-0.5">
                          <XIcon className="h-3 w-3" />
                        </button>
                      )}
                    </Badge>
                  ))}
                  {(form.tags || []).length === 0 && (
                    <span className="text-xs text-slate-400 self-center">Nenhuma etiqueta.</span>
                  )}
                </div>
                {!disabled && (
                  <>
                    <div className="mt-2 flex gap-2">
                      <Input
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                        placeholder="Digite e pressione Enter"
                        data-testid="tag-input"
                        className="flex-1"
                      />
                      <Button type="button" size="sm" variant="outline" onClick={() => addTag()} data-testid="add-tag-btn">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {COMMON_TAGS.filter((t) => !(form.tags || []).includes(t)).slice(0, 8).map((t) => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => addTag(t)}
                          data-testid={`suggest-tag-${t}`}
                          className="text-[11px] px-2 py-0.5 rounded-full border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                        >
                          + {t}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </Section>

          <Section title="Observações Internas (Equipe)">
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
              Notas internas com autor e data. Não aparecem em PDFs enviados ao cliente.
            </p>
            {canEdit && (
              <div className="flex gap-2 mb-4">
                <Textarea
                  value={newObs}
                  onChange={(e) => setNewObs(e.target.value)}
                  rows={2}
                  placeholder="Ex: Cliente ligou pedindo revisão dos documentos até sexta"
                  data-testid="new-observation-input"
                  className="flex-1"
                />
                <Button
                  onClick={submitObs}
                  disabled={obsSubmitting || !newObs.trim()}
                  data-testid="submit-observation-btn"
                  className="bg-[#0F2942] hover:bg-[#173E63] text-white self-start"
                >
                  <MessageSquare className="h-4 w-4 mr-1" />
                  {obsSubmitting ? "..." : "Adicionar"}
                </Button>
              </div>
            )}
            <ul className="space-y-2" data-testid="observations-list">
              {observations.length === 0 && (
                <li className="text-sm text-slate-400 dark:text-slate-500 text-center py-4">
                  Nenhuma observação registrada.
                </li>
              )}
              {observations.map((o) => (
                <li key={o.id} className="border border-slate-100 dark:border-slate-800 rounded-lg p-3 bg-slate-50 dark:bg-slate-800/50 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-800 dark:text-slate-200 whitespace-pre-wrap">{o.text}</p>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1.5">
                      <span className="font-medium">{o.author_name}</span>
                      {" • "}
                      {formatDateTime(o.created_at)}
                    </p>
                  </div>
                  {isAdmin && (
                    <button
                      onClick={() => removeObs(o.id)}
                      data-testid={`delete-obs-${o.id}`}
                      className="text-slate-400 hover:text-red-600 p-1"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </Section>

          <Section title="Observações (Perfil do Cliente)">
            <Textarea
              value={form.observacoes}
              disabled={disabled}
              onChange={(e) => set("observacoes", e.target.value)}
              rows={4}
              data-testid="cf-observacoes"
              placeholder="Notas sobre o perfil do cliente, preferências, histórico..."
            />
          </Section>

          {canEdit && (
            <div className="flex justify-end">
              <Button
                onClick={handleSave}
                disabled={saving}
                data-testid="save-client-button-bottom"
                className="bg-[#0F2942] hover:bg-[#173E63] text-white"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? "Salvando..." : "Salvar Cliente"}
              </Button>
            </div>
          )}

          {audit.by && audit.at && (
            <div
              className="text-xs text-slate-500 border-t border-slate-200 pt-4"
              data-testid="client-audit-info"
            >
              Última atualização feita por:{" "}
              <span className="font-medium text-[#0F2942]">{audit.by}</span> em{" "}
              <span className="font-medium text-[#0F2942]">
                {formatDateTime(audit.at)}
              </span>
            </div>
          )}
        </TabsContent>

        <TabsContent value="ficha" className="mt-4">
          <ReportDetail clientIdProp={clientId} embedded />
        </TabsContent>

        <TabsContent value="contrato" className="mt-4">
          <ClientFiles
            clientId={clientId}
            category="contrato_social"
            title="Contrato Social"
            description="Histórico permanente de contratos sociais e alterações contratuais. Cada versão é preservada — novos anexos não substituem os anteriores."
          />
        </TabsContent>

        <TabsContent value="documentos" className="mt-4">
          <ClientFiles
            clientId={clientId}
            category="documento"
            title="Documentos"
            description="Documentos gerais do cliente. Renomeie para facilitar a busca. Ex: 'RG — João', 'Procuração — 2026'."
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
